// lib/inspiration_cache_context.js

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRouter } from 'next/router';
import Qs from 'qs';

export const GalleryCacheContext = createContext({});
export const ApplicationPhotoCacheContext = createContext({});

const getPath = (url = '') => url.split('?')[0].replace(/\/+$/, '') || '/';

const getQuery = (url = '') => {
  const query = url.split('?')[1] || '';
  return Qs.parse(query, { arrayFormat: 'brackets' });
};

const isGalleryPath = (path = '') =>
  /\/inspiration\/(galleries|gallery)(\/|$)/.test(path);

const isApplicationPhotoPath = (path = '') =>
  /\/inspiration\/application_photos?(\/|$)/.test(path);

const getFeature = (url = '') => {
  const path = getPath(url);

  // Application photos accessed through a gallery belong to the gallery flow.
  if (
    isGalleryPath(path) ||
    (isApplicationPhotoPath(path) && /[?&]gallery_id(?:=|&|$)/.test(url))
  ) {
    return 'gallery';
  }

  if (isApplicationPhotoPath(path)) {
    return 'applicationPhoto';
  }

  return null;
};

const isListingPath = (feature, path) => {
  if (feature === 'gallery') {
    return path.startsWith('/lex-cms/inspiration/galleries');
  }

  if (feature === 'applicationPhoto') {
    return path.startsWith('/lex-cms/inspiration/application_photos');
  }

  return false;
};

const pathNameMap = {
  '/lex-cms/inspiration/galleries': (q, slug) => ({
    pathname: '/inspiration/galleries/[index]',
    query: { index: slug, ...q }
  }),
  '/lex-cms/inspiration/gallery': (q, slug) => ({
    pathname: '/inspiration/gallery/[show]',
    query: { show: slug }
  }),
  '/lex-cms/inspiration/application_photos': (q, slug) => ({
    pathname: '/inspiration/application_photos/[index]',
    query: { index: slug, ...q }
  }),
  '/lex-cms/inspiration/application_photo': (q, slug) => ({
    pathname: '/inspiration/application_photo/[show]',
    query: { show: slug }
  })
};

export function InspirationCacheWrapper(props) {
  const router = useRouter();

  // Gallery cache
  const [galleryItems, setGalleryItems] = useState(props.items || []);
  const [gallerySearch, setGallerySearch] = useState(props.search || {});
  const [galleryScroll, setGalleryScroll] = useState(0);
  const [galleryNextPage, setGalleryNextPage] = useState(props.nextPage);
  const [galleryTotalCount, setGalleryTotalCount] = useState(
    props.totalCount || 0
  );

  // Application photo cache
  const [photoItems, setPhotoItems] = useState(props.items || []);
  const [photoSearch, setPhotoSearch] = useState(props.search || {});
  const [photoScroll, setPhotoScroll] = useState(0);
  const [photoNextPage, setPhotoNextPage] = useState(props.nextPage);
  const [photoTotalCount, setPhotoTotalCount] = useState(
    props.totalCount || 0
  );

  // Independent filters for each feature
  const [galleryApplicationCategoryFilters, setGalleryApplicationCategoryFilters] =
    useState(props.applicationCategoryFilters || []);
  const [gallerySpaceFilters, setGallerySpaceFilters] =
    useState(props.spaceFilters || []);
  const [galleryProductTypeFilters, setGalleryProductTypeFilters] =
    useState(props.productTypeFilters || []);
  const [galleryBrandFilters, setGalleryBrandFilters] =
    useState(props.brandFilters || []);

  const [photoApplicationCategoryFilters, setPhotoApplicationCategoryFilters] =
    useState(props.applicationCategoryFilters || []);
  const [photoSpaceFilters, setPhotoSpaceFilters] =
    useState(props.spaceFilters || []);
  const [photoProductTypeFilters, setPhotoProductTypeFilters] =
    useState(props.productTypeFilters || []);
  const [photoBrandFilters, setPhotoBrandFilters] =
    useState(props.brandFilters || []);

  const cacheRef = useRef({});
  const pendingRestoreRef = useRef(null);

  useEffect(() => {
    cacheRef.current = {
      gallery: {
        items: galleryItems,
        scroll: galleryScroll
      },
      applicationPhoto: {
        items: photoItems,
        scroll: photoScroll
      }
    };
  }, [galleryItems, galleryScroll, photoItems, photoScroll]);

  const appendGalleries = useCallback((items) => {
    setGalleryItems((current) => [...current, ...items]);
  }, []);

  const appendPhotos = useCallback((items) => {
    setPhotoItems((current) => [...current, ...items]);
  }, []);

  useEffect(() => {
    if (!router) return;

    // The one and only beforePopState handler for both inspiration features.
  router.beforePopState(({ url, as }) => {
  

  // Keep the rest of your existing handler unchanged.
  const targetUrl = as || url;
  const targetPath = getPath(targetUrl);
  const targetFeature = getFeature(targetUrl);

  const currentFeature = getFeature(
    `${window.location.pathname}${window.location.search}`
  );

  if (
    targetFeature &&
    targetFeature === currentFeature &&
    isListingPath(targetFeature, targetPath) &&
    cacheRef.current[targetFeature]?.items?.length
  ) {
    const pathBase = targetPath.replace(/\/[^/]+$/, '');
    const getRouteConfig = pathNameMap[pathBase];

    if (getRouteConfig) {
      const query = getQuery(targetUrl);
      query.clientNavigation = 1;

      const slug = targetPath.split('/').filter(Boolean).pop();

      pendingRestoreRef.current = targetFeature;

      router.push(
        getRouteConfig(query, slug),
        as,
        { scroll: false }
      ).catch((error) => {
        pendingRestoreRef.current = null;
        console.error('[CACHE RESTORE] navigation failed', error);
      });

      // Prevent Next.js from handling the same popstate a second time.
      return false;
    }
  }
  return true;
 });
  const handleRouteChangeStart = (url) => {
  const currentUrl =
    `${window.location.pathname}${window.location.search}`;

  const currentPath = getPath(currentUrl);
  const currentFeature = getFeature(currentUrl);
  const targetPath = getPath(url);

    if (
      currentFeature &&
      isListingPath(currentFeature, currentPath) &&
      !isListingPath(currentFeature, targetPath)
    ) {
      const position = window.pageYOffset;

    
      if (currentFeature === 'gallery') {
        setGalleryScroll(position);
      } else {
        setPhotoScroll(position);
      }
    }

  // Restore when navigating back to a listing or following a breadcrumb.
  const targetFeature = getFeature(url);

  if (
    targetFeature &&
    targetFeature === currentFeature &&
    isListingPath(targetFeature, targetPath) &&
    !isListingPath(targetFeature, currentPath) &&
    cacheRef.current[targetFeature]?.items?.length
  ) {
    pendingRestoreRef.current = targetFeature;
  }
};;

    const handleRouteChangeComplete = (url) => {
  const feature = pendingRestoreRef.current;

  

  if (!feature) return;

  pendingRestoreRef.current = null;

  if (
    getFeature(url) !== feature ||
    !isListingPath(feature, getPath(url))
  ) {
    return;
  }

  const position = cacheRef.current[feature]?.scroll || 0;


  requestAnimationFrame(() => {
    window.scrollTo({
      top: position,
      behavior: 'instant'
    });

  });
};

    const handleRouteChangeError = () => {
      pendingRestoreRef.current = null;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);

      // Reset the single shared handler when the wrapper unmounts.
      router.beforePopState(() => true);
    };
  }, [router]);

  const galleryValue = {
    items: galleryItems,
    setItems: setGalleryItems,
    appendGalleries,
    search: gallerySearch,
    setSearch: setGallerySearch,
    nextPage: galleryNextPage,
    setNextPage: setGalleryNextPage,
    totalCount: galleryTotalCount,
    setTotalCount: setGalleryTotalCount,
    applicationCategoryFilters: galleryApplicationCategoryFilters,
    setApplicationCategoryFilters: setGalleryApplicationCategoryFilters,
    spaceFilters: gallerySpaceFilters,
    setSpaceFilters: setGallerySpaceFilters,
    productTypeFilters: galleryProductTypeFilters,
    setProductTypeFilters: setGalleryProductTypeFilters,
    brandFilters: galleryBrandFilters,
    setBrandFilters: setGalleryBrandFilters,
    scroll: galleryScroll
  };

  const photoValue = {
    items: photoItems,
    setItems: setPhotoItems,
    appendPhotos,
    search: photoSearch,
    setSearch: setPhotoSearch,
    nextPage: photoNextPage,
    setNextPage: setPhotoNextPage,
    totalCount: photoTotalCount,
    setTotalCount: setPhotoTotalCount,
    applicationCategoryFilters: photoApplicationCategoryFilters,
    setApplicationCategoryFilters: setPhotoApplicationCategoryFilters,
    spaceFilters: photoSpaceFilters,
    setSpaceFilters: setPhotoSpaceFilters,
    productTypeFilters: photoProductTypeFilters,
    setProductTypeFilters: setPhotoProductTypeFilters,
    brandFilters: photoBrandFilters,
    setBrandFilters: setPhotoBrandFilters,
    scroll: photoScroll
  };

  return (
    <ApplicationPhotoCacheContext.Provider value={photoValue}>
      <GalleryCacheContext.Provider value={galleryValue}>
        {props.children}
      </GalleryCacheContext.Provider>
    </ApplicationPhotoCacheContext.Provider>
  );
}
